/**
 * InventoryInsightsPanel — AI-powered inventory analysis card.
 * Displays categorized insights with severity colors and affected items.
 * Collapsible, with loading skeleton and graceful error states.
 */

import { useState } from "react";
import {
  InventoryInsightsResponse,
  InventoryInsight,
} from "../utils/PowerBIApiCall";

interface InventoryInsightsPanelProps {
  data: InventoryInsightsResponse | null;
  loading: boolean;
  error: string | null;
  onRun: () => void;
}

const CATEGORY_META: Record<
  string,
  { label: string; color: string; iconPath: string }
> = {
  stockout_risk: {
    label: "Stockout Risk",
    color: "text-red-600",
    iconPath:
      "M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z",
  },
  overstock: {
    label: "Overstock",
    color: "text-amber-600",
    iconPath: "M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4",
  },
  dead_stock: {
    label: "Dead Stock",
    color: "text-rose-600",
    iconPath:
      "M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16",
  },
  reorder: {
    label: "Reorder Needed",
    color: "text-blue-600",
    iconPath:
      "M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15",
  },
  value_concentration: {
    label: "Value Concentration",
    color: "text-purple-600",
    iconPath:
      "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6m6 0h6m-6 0V9m6 10v-4a2 2 0 00-2-2h-2a2 2 0 00-2 2v4m6 0h2a2 2 0 002-2v-6a2 2 0 00-2-2h-2a2 2 0 00-2 2v6",
  },
  expiration: {
    label: "Expiration Alert",
    color: "text-orange-600",
    iconPath: "M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z",
  },
  general: {
    label: "General",
    color: "text-gray-600",
    iconPath: "M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
  },
};

const SEVERITY_STYLES: Record<string, string> = {
  critical: "bg-red-50 border-red-200",
  warning: "bg-amber-50 border-amber-200",
  info: "bg-blue-50/50 border-blue-100",
};

const SEVERITY_BADGE: Record<string, string> = {
  critical: "bg-red-100 text-red-700",
  warning: "bg-amber-100 text-amber-700",
  info: "bg-blue-100 text-blue-700",
};

export function InventoryInsightsPanel({
  data,
  loading,
  error,
  onRun,
}: InventoryInsightsPanelProps) {
  const [expanded, setExpanded] = useState(false);
  const hasResults = data || loading || error;

  return (
    <div className="bg-white rounded-xl border border-gray-200/70 shadow-card overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 flex items-center justify-between border-b border-gray-100">
        <button
          onClick={() => hasResults && setExpanded(!expanded)}
          className={`flex items-center gap-2.5 ${hasResults ? "cursor-pointer" : "cursor-default"}`}
        >
          <h2 className="text-lg font-semibold text-gray-900">
            Inventory Insights
          </h2>
          {loading && (
            <span className="text-2xs bg-blue-50 text-blue-500 px-1.5 py-0.5 rounded font-medium">
              analyzing...
            </span>
          )}
          {data?.cached && !loading && (
            <span className="text-2xs bg-gray-100 text-gray-400 px-1.5 py-0.5 rounded">
              cached
            </span>
          )}
          {data && !loading && (
            <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full font-medium">
              {data.insights.length}
            </span>
          )}
          {hasResults && (
            <svg
              className={`w-4 h-4 text-gray-400 transition-transform ${expanded ? "rotate-180" : ""}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          )}
        </button>

        <button
          onClick={() => {
            onRun();
            setExpanded(true);
          }}
          disabled={loading}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200/70 bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <svg
            className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            {loading ? (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            ) : (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 10V3L4 14h7v7l9-11h-7z"
              />
            )}
          </svg>
          {loading ? "Analyzing..." : data ? "Re-run" : "Run Analysis"}
        </button>
      </div>

      {/* Body */}
      {expanded && (
        <div className="px-6 py-4">
          {loading && <InsightsLoadingSkeleton />}
          {error && !loading && <InsightsError message={error} />}
          {data && !loading && (
            <div className="space-y-3">
              {data.insights.map((insight, idx) => (
                <InsightCard key={idx} insight={insight} />
              ))}
              <p className="text-2xs text-gray-400 text-right mt-2">
                Generated {new Date(data.generatedAt).toLocaleTimeString()}{" "}
                &mdash; {data.dataSnapshot.totalItems} items analyzed
              </p>
            </div>
          )}
          {!loading && !error && !data && (
            <p className="text-sm text-gray-400 text-center py-4">
              Click &ldquo;Run Analysis&rdquo; to generate AI-powered inventory
              insights.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function InsightCard({ insight }: { insight: InventoryInsight }) {
  const meta = CATEGORY_META[insight.category] || CATEGORY_META.general;
  const severityStyle =
    SEVERITY_STYLES[insight.severity] || SEVERITY_STYLES.info;
  const badgeStyle = SEVERITY_BADGE[insight.severity] || SEVERITY_BADGE.info;

  return (
    <div className={`rounded-xl border p-4 ${severityStyle}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <svg
              className={`w-3.5 h-3.5 flex-shrink-0 ${meta.color}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d={meta.iconPath}
              />
            </svg>
            <span
              className={`text-2xs font-semibold uppercase tracking-wider ${meta.color}`}
            >
              {meta.label}
            </span>
            <span
              className={`text-2xs font-bold px-1.5 py-0.5 rounded ${badgeStyle}`}
            >
              {insight.severity}
            </span>
          </div>
          <h3 className="text-sm font-semibold text-gray-900">
            {insight.title}
          </h3>
          <p className="text-xs text-gray-600 mt-1 leading-relaxed">
            {insight.description}
          </p>
          {insight.items && insight.items.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {insight.items.map((itemId) => (
                <span
                  key={itemId}
                  className="text-2xs font-mono bg-white/60 text-gray-500 px-1.5 py-0.5 rounded border border-gray-200"
                >
                  {itemId}
                </span>
              ))}
            </div>
          )}
        </div>
        {insight.metric && (
          <div className="text-right flex-shrink-0">
            <span className="text-sm font-bold text-gray-900 whitespace-nowrap tabular-nums">
              {insight.metric}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

function InsightsLoadingSkeleton() {
  return (
    <div className="space-y-3">
      {[...Array(3)].map((_, i) => (
        <div
          key={i}
          className="rounded-xl border border-gray-100 p-4 animate-pulse"
        >
          <div className="flex items-center gap-2 mb-2">
            <div className="h-3 w-20 bg-gray-200 rounded" />
            <div className="h-3 w-12 bg-gray-100 rounded" />
          </div>
          <div className="h-4 w-3/4 bg-gray-200 rounded mb-2" />
          <div className="h-3 w-full bg-gray-100 rounded" />
        </div>
      ))}
      <p className="text-2xs text-gray-400 text-center mt-2">
        Analyzing inventory data with AI...
      </p>
    </div>
  );
}

function InsightsError({ message }: { message: string }) {
  return (
    <div className="text-center py-4">
      <p className="text-sm text-gray-400">
        Unable to generate insights. {message}
      </p>
    </div>
  );
}
